import { useQueryClient } from '@tanstack/react-query';
import { makeRivieraDataQueryPackage } from 'js/file_viewer/core/queries/riviera_base';
import { rivieraClient } from 'js/file_viewer/core/riviera/api';
import { transformFont } from 'js/file_viewer/core/riviera/transform';
import { RivieraStatus } from 'js/file_viewer/core/riviera/types';
import { TEXT_BATCH_SIZE, calculatePageRange } from 'js/file_viewer/core/utils/functions';
import { generateDocThumbnailUrl } from 'js/file_viewer/core/utils/urls';
import { useStabilizedCallback } from 'js/file_viewer/core/utils/useStabilizedCallback';
import { PreviewsEventLogger, PreviewsTimingEvents } from 'js/file_viewer/logging/performance';
const THUMBNAIL_PREFETCH_COUNT = 11;
const uncompressDimensions = (compressedDimensions) => {
    const uncompressedDimensions = [];
    for (const [w, h, count] of compressedDimensions) {
        for (let i = 0; i < count; i++) {
            uncompressedDimensions.push([w, h]);
        }
    }
    return uncompressedDimensions;
};
const unpackFonts = (fontInfo) => {
    const fonts = {};
    for (const font of fontInfo) {
        fonts[font.f] = transformFont(font);
    }
    return fonts;
};
const fetchSsrDocMetadata = async (args) => {
    var _a, _b, _c, _d;
    const { previewMetadata } = args;
    const content = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.content;
    if (!content || (content['.tag'] !== 'ssr_doc' && content['.tag'] !== 'cloud_doc')) {
        // useSsrDocDataQuery is disabled when !previewMetadata, so this should not fire in that case.
        throw new Error('Invalid content metadata when fetching document.');
    }
    if (!content.image_url_tmpl || !content.text_url_tmpl) {
        throw new Error('Must specify image and text URLs in preview metadata.');
    }
    const [startPage, endPage] = args.pageRange;
    const fetchMetadata = startPage === 0;
    const result = await rivieraClient.fetchDocMetadata(content.text_url_tmpl, fetchMetadata, startPage, endPage);
    if (result.error && result.error.statusCode === RivieraStatus.PasswordProtected) {
        return {
            metadata: {
                isPasswordProtected: true,
                needsPassword: true,
                dimensions: [],
                pageCount: 0,
            },
            pageData: { text: [] },
        };
    }
    else if (result.error) {
        throw result.error;
    }
    let metadata = undefined;
    if (fetchMetadata) {
        const serverMetadata = result.data.metadata;
        if (!serverMetadata) {
            throw new Error('Missing metadata in Riviera response');
        }
        metadata = {
            pageCount: serverMetadata.page_count,
            needsPassword: false,
            isPasswordProtected: false,
            dimensions: uncompressDimensions(serverMetadata.dimensions),
        };
        PreviewsEventLogger.mark(PreviewsTimingEvents.FVSDK_DOC_METADATA_LOAD);
    }
    const fonts = unpackFonts(((_a = result.data.text) === null || _a === void 0 ? void 0 : _a.fonts) || []);
    const numPages = endPage - startPage;
    const text = new Array(numPages);
    const links = new Array(numPages);
    for (let i = 0; i < numPages; i++) {
        const pageIndex = (startPage + i).toString();
        text[i] = (_b = result.data.text) === null || _b === void 0 ? void 0 : _b.pages[pageIndex];
        links[i] = (_d = (_c = result.data.text) === null || _c === void 0 ? void 0 : _c.links) === null || _d === void 0 ? void 0 : _d[pageIndex];
    }
    if (startPage === 0 && result.data.metadata) {
        const supportedWidths = 'supported_widths' in content ? content.supported_widths : undefined;
        const end = (metadata === null || metadata === void 0 ? void 0 : metadata.pageCount)
            ? Math.min(metadata.pageCount, THUMBNAIL_PREFETCH_COUNT)
            : THUMBNAIL_PREFETCH_COUNT;
        for (let i = 0; i < end; i++) {
            const thumbnailUrl = generateDocThumbnailUrl(content.image_url_tmpl, i, supportedWidths);
            rivieraClient.fetchImage(thumbnailUrl);
        }
    }
    return {
        metadata,
        pageData: {
            fonts,
            text,
            links,
        },
    };
};
const ssrDocQueryPackage = makeRivieraDataQueryPackage({
    fileTypeSpecificQueryFn: fetchSsrDocMetadata,
    fieldName: 'ssr_doc',
});
const enabledForPreviewMetadata = (previewMetadata) => {
    const content = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.content;
    // Cloud_docs generally don't need to call Riviera, as we only show 'Open in new tab' instead of previewing the content.
    // However, Paper docs (a type of cloud_doc) are previewed in scl/fo, so we need to fetch Riviera data.
    const isPaper = (content === null || content === void 0 ? void 0 : content['.tag']) === 'cloud_doc' && !!(content === null || content === void 0 ? void 0 : content.image_url_tmpl) && !!(content === null || content === void 0 ? void 0 : content.text_url_tmpl);
    return (content === null || content === void 0 ? void 0 : content['.tag']) === 'ssr_doc' || isPaper;
};
export const useSsrDocMetadataQuery = (previewKey, previewMetadata) => {
    const pageRange = calculatePageRange(0);
    return ssrDocQueryPackage.useQuery({
        apiArg: {
            pageRange,
            previewKey,
            previewMetadata,
        },
        pkgArg: null,
    }, {
        enabled: !!previewKey && enabledForPreviewMetadata(previewMetadata),
    });
};
export const useSsrDocPageData = (previewKey, previewMetadata, pageIndex, enabled) => {
    var _a;
    const pageRange = calculatePageRange(pageIndex);
    const pages = ssrDocQueryPackage.useQuery({
        apiArg: {
            pageRange,
            previewKey,
            previewMetadata,
        },
        pkgArg: null,
    }, { enabled: !!previewKey && enabled && enabledForPreviewMetadata(previewMetadata) });
    if (pages.isLoading || !pages.data) {
        return undefined;
    }
    const data = pages.data.apiData.pageData;
    const chunkIndex = pageIndex - pageRange[0];
    return {
        text: data.text[chunkIndex],
        links: (_a = data.links) === null || _a === void 0 ? void 0 : _a[chunkIndex],
        fonts: data.fonts,
    };
};
export const useFetchSsrDocPageData = (previewKey, previewMetadata, pageCount) => {
    const queryClient = useQueryClient();
    return useStabilizedCallback(async () => {
        if (!pageCount) {
            return;
        }
        let pageDataPromise;
        let fetchRangeStart;
        const chunkedPromises = [];
        for (let i = 0; i < pageCount; i += TEXT_BATCH_SIZE) {
            const pageRange = calculatePageRange(i);
            const queryKey = ssrDocQueryPackage.getQueryKey({
                previewKey,
                previewMetadata,
                pageRange,
            });
            // Don't refetch existing data
            if (queryClient.getQueryState(queryKey)) {
                chunkedPromises.push(queryClient.ensureQueryData(queryKey));
                continue;
            }
            else if (!pageDataPromise) {
                fetchRangeStart = i;
                pageDataPromise = fetchSsrDocMetadata({
                    previewMetadata,
                    previewKey,
                    pageRange: [i, pageCount],
                });
            }
            const getChunkData = async () => {
                var _a;
                // Typescript isn't smart enough to realize pageDataPromise is defined here.
                // This should be fixed if we ever update to TS 5.4:
                // https://www.typescriptlang.org/docs/handbook/release-notes/typescript-5-4.html#preserved-narrowing-in-closures-following-last-assignments
                const pageData = await pageDataPromise;
                const resultStart = pageRange[0] - fetchRangeStart;
                const resultEnd = pageRange[1] - fetchRangeStart;
                return {
                    apiData: {
                        pageData: {
                            text: pageData.pageData.text.slice(resultStart, resultEnd),
                            links: (_a = pageData.pageData.links) === null || _a === void 0 ? void 0 : _a.slice(resultStart, resultEnd),
                            fonts: pageData.pageData.fonts,
                        },
                        metadata: pageData.metadata,
                    },
                    pkgData: null,
                };
            };
            chunkedPromises.push(queryClient.fetchQuery({ queryKey, queryFn: getChunkData }));
        }
        return Promise.all(chunkedPromises).then(() => { });
    });
};
// A little weird since the find in doc controller needs this but it isn't in
// a React context.
export const useGetSsrDocText = (previewKey, previewMetadata, pageCount) => {
    const queryClient = useQueryClient();
    return useStabilizedCallback(() => {
        if (!pageCount) {
            return [];
        }
        const textData = [];
        for (let i = 0; i < pageCount; i += TEXT_BATCH_SIZE) {
            const pageRange = calculatePageRange(i);
            const textChunk = ssrDocQueryPackage.getQueryData(queryClient, {
                pageRange,
                previewKey,
                previewMetadata,
            });
            if (textChunk) {
                textData.push(...textChunk.apiData.pageData.text);
            }
            else {
                textData.push(...new Array(Math.min(pageCount - i, TEXT_BATCH_SIZE)));
            }
        }
        return textData;
    });
};
export const fetchSsrDocRivieraData = (queryClient, previewKey, previewMetadata) => {
    if (!previewKey || !enabledForPreviewMetadata(previewMetadata)) {
        return Promise.resolve();
    }
    const pageRange = calculatePageRange(0);
    return ssrDocQueryPackage.fetchQuery(queryClient, {
        apiArg: {
            previewKey,
            previewMetadata,
            pageRange,
        },
        pkgArg: null,
    });
};
export const prefetchSsrDocRivieraData = (queryClient, previewKey, previewMetadata) => {
    if (!previewKey || !enabledForPreviewMetadata(previewMetadata)) {
        return Promise.resolve();
    }
    const pageRange = calculatePageRange(0);
    return ssrDocQueryPackage.prefetchQuery(queryClient, {
        apiArg: {
            previewKey,
            previewMetadata,
            pageRange,
        },
        pkgArg: null,
    });
};
//# sourceMappingURL=ssr_doc.preenvesnextjs.map
